import React from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { Icon } from '@devnw/icons'
import { asUnified, ut } from '@devnw/theme'
import { ROUTE_PERMISSIONS } from '../auth/routePermissions'
import { PamError } from '../components/PamStateComponents'
import type { PermissionsStatus } from '../auth/AuthContext'
import type { AdminRoleKind } from '../api/types'

/**
 * The admin nav list: the permissions banner (IMPORTANT-2) plus the
 * NAV_GROUPS.map(...) render, extracted out of AdminLayout (Task 3,
 * OIDCPAM Gate-6 Phase 8 AppShell migration) so the desktop permanent
 * sidebar and the mobile off-canvas drawer render the SAME nav — they can
 * never diverge because there is only one implementation of it.
 */

interface NavItem {
  to: string
  label: string
  /**
   * Web Awesome / Font Awesome-free kebab-case icon name rendered via
   * @devnw/icons' <Icon>. Best-effort semantic equivalents of the
   * pre-migration MUI icon glyphs — not audited icon-by-icon against the
   * underlying icon set, purely decorative (rendered `ariaHidden`), so an
   * unresolved name degrades to nothing rendered rather than breaking nav.
   */
  icon: string
}

interface NavGroup {
  label: string | null
  items: NavItem[]
}

/**
 * Role-aware nav filter. Keyed by route path; value is the set of
 * AdminRoleKind values that may see that item.
 *
 * Rules (applied in AdminNav render):
 *  - Empty array → visible to ALL admin roles (no restriction).
 *  - Non-empty array → visible only to the listed roles (and system admin).
 *  - If a route is absent from this map it defaults to visible to all.
 *
 * UX ONLY — the server enforces authz via Epic-11 adminRoleGate.
 * Hiding a nav item is cosmetic. Server blocks any role reaching a
 * restricted route directly by URL.
 */
const NAV_ROLE_FILTER: Record<string, AdminRoleKind[]> = {
  // ── Admin group (visible to all) ──────────────────────────────────────────
  '/admin/tenants': [],
  '/admin/users': [],
  '/admin/connectors': [],
  // Gated by PermTenantSecretsManage server-side, which only the built-in
  // AdminRoleSecurity role carries (verified against pam_permissions.go) —
  // unlike connectors, this is NOT visible to every admin role.
  '/admin/secrets-provider': ['security-admin'],
  // Gated by PermTenantMailManage server-side — same AdminRoleSecurity-only
  // gate as secrets-provider above (verified against pam_permissions.go).
  '/admin/mail-provider': ['security-admin'],
  '/admin/clients': [],
  '/admin/mtls-cas': [],
  '/admin/audit': ['audit-admin'],
  '/admin/api-docs': [],
  // Same server permission as /admin/audit (PermAuditQuery).
  '/admin/audit-chain-integrity': ['audit-admin'],

  // ── Identity group — security-admin only ──────────────────────────────────
  '/admin/subjects': ['security-admin'],
  '/admin/resources': ['security-admin'],
  '/admin/apps': ['security-admin'],
  '/admin/entitlements': ['security-admin'],

  // ── Account group (visible to all — self-service, not tenant-wide admin) ──
  '/admin/me': [],
  '/admin/security-keys': [],

  // ── Access group ──────────────────────────────────────────────────────────
  '/admin/pam-overview': [],
  '/admin/policies': ['policy-admin'],
  '/admin/stepup-policy': ['policy-admin'],
  '/admin/access-requests': ['security-admin'],
  '/admin/break-glass': ['security-admin'],
  '/admin/emergency-roles': ['security-admin'],

  // ── Sessions group — security-admin only ─────────────────────────────────
  '/admin/brokered-sessions': ['security-admin'],
  '/admin/session-control-policy': ['policy-admin'],
  '/admin/ephemeral-creds': ['security-admin'],

  // ── Monitoring group ──────────────────────────────────────────────────────
  '/admin/cae': ['audit-admin'],
  '/admin/resilience': ['audit-admin'],
  '/admin/audit-evidence': ['audit-admin'],

  // ── Governance group — all admin roles ────────────────────────────────────
  '/admin/governance/discovery': [],
  '/admin/governance/campaigns': [],
  '/admin/governance/roles': [],
  '/admin/governance/dual-auth': [],
  // Role registry — gated by gov:roledef.manage (seeded into audit-admin).
  '/admin/governance/role-registry': ['audit-admin'],
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [
      { to: '/admin/tenants', label: 'Tenants', icon: 'building' },
      { to: '/admin/users', label: 'Users & Roles', icon: 'users' },
      { to: '/admin/connectors', label: 'Connectors', icon: 'link' },
      {
        to: '/admin/secrets-provider',
        label: 'Secrets Provider',
        icon: 'key',
      },
      {
        to: '/admin/mail-provider',
        label: 'Mail Provider',
        icon: 'envelope',
      },
      { to: '/admin/clients', label: 'Clients', icon: 'laptop' },
      { to: '/admin/mtls-cas', label: 'mTLS CAs', icon: 'shield-halved' },
      { to: '/admin/audit', label: 'Audit Log', icon: 'clipboard-list' },
      { to: '/admin/api-docs', label: 'API Docs', icon: 'file-lines' },
    ],
  },
  {
    label: 'Identity',
    items: [
      { to: '/admin/subjects', label: 'Subjects', icon: 'id-badge' },
      { to: '/admin/resources', label: 'Resources', icon: 'database' },
      { to: '/admin/apps', label: 'Apps', icon: 'sitemap' },
      { to: '/admin/entitlements', label: 'Entitlements', icon: 'key' },
    ],
  },
  {
    // Task 10/11 (fix-wave 2026-07-24): self-service account surfaces, kept
    // separate from "Access" (tenant-wide PAM admin surfaces like PAM
    // Overview/Policies/Break-Glass) — filing "My passkeys" there previously
    // made a self-service, own-credentials-only page read as an app-wide
    // credential inventory. See WebAuthnCredentials.tsx's heading comment.
    label: 'Account',
    items: [
      { to: '/admin/me', label: 'My Profile', icon: 'id-card' },
      { to: '/admin/security-keys', label: 'My passkeys', icon: 'fingerprint' },
    ],
  },
  {
    label: 'Access',
    items: [
      { to: '/admin/pam-overview', label: 'PAM Overview', icon: 'gauge' },
      { to: '/admin/policies', label: 'Policies', icon: 'scroll' },
      { to: '/admin/stepup-policy', label: 'Step-up Policy', icon: 'lock' },
      {
        to: '/admin/access-requests',
        label: 'Access Requests',
        icon: 'square-check',
      },
      {
        to: '/admin/break-glass',
        label: 'Break-Glass',
        icon: 'triangle-exclamation',
      },
      {
        to: '/admin/emergency-roles',
        label: 'Emergency Roles',
        icon: 'shield',
      },
    ],
  },
  {
    label: 'Sessions',
    items: [
      {
        to: '/admin/brokered-sessions',
        label: 'Brokered Sessions',
        icon: 'heart-pulse',
      },
      {
        to: '/admin/session-control-policy',
        label: 'Session Controls',
        icon: 'sliders',
      },
      {
        to: '/admin/ephemeral-creds',
        label: 'Ephemeral Creds',
        icon: 'ticket',
      },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      {
        to: '/admin/cae',
        label: 'CAE Dashboard',
        icon: 'shield-halved',
      },
      {
        to: '/admin/resilience',
        label: 'PAM Resilience',
        icon: 'heart-pulse',
      },
      {
        to: '/admin/audit-evidence',
        label: 'Audit Evidence',
        icon: 'magnifying-glass',
      },
      {
        to: '/admin/audit-chain-integrity',
        label: 'Chain Integrity',
        icon: 'link-slash',
      },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        to: '/admin/governance/discovery',
        label: 'Discovery',
        icon: 'magnifying-glass',
      },
      {
        to: '/admin/governance/campaigns',
        label: 'Campaigns',
        icon: 'list-check',
      },
      {
        to: '/admin/governance/roles',
        label: 'Admin Roles',
        icon: 'user-shield',
      },
      {
        to: '/admin/governance/dual-auth',
        label: 'Dual-Auth Queue',
        icon: 'check-to-slot',
      },
      {
        to: '/admin/governance/role-registry',
        label: 'Role Registry',
        icon: 'book',
      },
    ],
  },
]

/**
 * Routes that require NO admin permission at all beyond the outer
 * `requireAdmin` gate — App.tsx never wraps '/admin/me' or
 * '/admin/security-keys' in a ProtectedRoute `requiredPermission`, because
 * they are self-service (own-profile / own-passkeys) surfaces backed by
 * `me*`/owner-scoped endpoints. They must NEVER be permission-filtered:
 * hiding them behind a `permissions` check (null OR resolved-empty) is what
 * produced CRITICAL-1's "ordinary admin logs in and can do nothing" —
 * these two are exactly the pages such an admin needs in order to see
 * ANYTHING, including while whoami is unresolved or has failed.
 */
const SELF_SERVICE_ROUTES: ReadonlySet<string> = new Set([
  '/admin/me',
  '/admin/security-keys',
])

/**
 * Returns true if the nav item at `route` should be visible for the given
 * caller, driven by the SERVER-RESOLVED effective permission set (GET
 * /v1/admin/whoami) rather than the unverified JWT `admin_role` claim.
 *
 * CRITICAL-1 (fix-wave 2026-07-24) rewrite — the Task 9 fail-closed rewrite
 * over-corrected: it treated `permissions === []` (a real, resolved,
 * genuinely-empty set — e.g. THREADID_STRICT_ADMIN_ROLES' default-true
 * strict mode with no admin_role claim and no system_admin allow-listing,
 * see admin_authz_helpers.go's effectiveAdminPermissions) identically to
 * `permissions === null` (unresolved). But the SERVER does not gate
 * base-admin routes (tenants/users/connectors/.../pam-overview/security-keys
 * — anything absent from ROUTE_PERMISSIONS) on the resolved permission set
 * at all: checkPathTenant's isTenantAdmin only checks callerSlug ==
 * pathTenantID, no permission or strict-mode check. So an ordinary
 * Terraform-provisioned admin (roles:["admin"], no AdminRoleAssignment, not
 * system_admin-listed) resolves permissions:[] and adminRole:null and is
 * STILL authorized for every one of those routes server-side — hiding them
 * left that admin with a completely empty nav. Rules, checked in order:
 *
 *  0. `route` is a self-service route (SELF_SERVICE_ROUTES) → ALWAYS
 *     visible, regardless of permissions/adminRole. Checked first so no
 *     whoami failure or genuinely-empty resolution can ever hide it.
 *  1. `permissions === null` (unresolved — see PermissionsStatus; the ONLY
 *     "we don't know yet" state) → HIDDEN.
 *  2. `route` has a dedicated Permission in ROUTE_PERMISSIONS (the
 *     fine-grained catalog from pam_permissions.go) → visible iff
 *     `permissions` includes that exact string. This is authoritative and
 *     does NOT fall through to the role-kind map below — a resolved-empty
 *     set correctly hides these (the server's enforcePermission would 403).
 *  3. `route` absent from BOTH ROUTE_PERMISSIONS and NAV_ROLE_FILTER
 *     (unknown route) → HIDDEN.
 *  4. `adminRole === null` (genuinely unscoped/backward-compat admin —
 *     matches the server's checkPathTenant, which imposes no permission
 *     check on these routes at all) → visible, EVEN when `permissions` is
 *     `[]`. This is the CRITICAL-1 fix: emptiness no longer implies
 *     "unresolved" for base-gated routes.
 *  5. `NAV_ROLE_FILTER[route]` is an empty array (no restriction beyond
 *     being a resolved admin) → visible.
 *  6. `NAV_ROLE_FILTER[route]` includes `adminRole` → visible. A caller
 *     carrying a SCOPED admin_role (not null) that isn't in the allow-list
 *     is still correctly restricted here — CRITICAL-1 only relaxed the
 *     null-adminRole/empty-permissions combination, not role scoping.
 *  7. Otherwise → HIDDEN.
 *
 * Exported for direct unit testing (also re-exported from AdminLayout.tsx,
 * which __tests__/AdminLayout.test.tsx imports it from).
 */
export function isNavItemVisible(
  route: string,
  adminRole: string | null,
  permissions: string[] | null
): boolean {
  if (SELF_SERVICE_ROUTES.has(route)) return true

  if (permissions === null) return false

  const requiredPermission = ROUTE_PERMISSIONS[route]
  if (requiredPermission !== undefined) {
    return permissions.includes(requiredPermission)
  }

  const allowedRoles = NAV_ROLE_FILTER[route]
  if (allowedRoles === undefined) return false
  if (adminRole === null) return true
  if (allowedRoles.length === 0) return true
  return allowedRoles.includes(adminRole as AdminRoleKind)
}

// ---------------------------------------------------------------------------
// Themed nav (styled-components + @devnw/theme tokens). All theme lookups go
// through `ut(asUnified(theme))` or optional-chained `asUnified(theme)`
// reads with literal fallbacks, so this degrades gracefully rather than
// throwing if ever rendered without a styled-components ThemeProvider.
// ---------------------------------------------------------------------------

const GroupHeader = styled.div`
  margin-top: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

// IMPORTANT-2 (fix-wave 2026-07-24): wraps the PamError banner shown when
// permissionsStatus === 'error' so a transient whoami failure is surfaced IN
// the shell instead of silently collapsing to an empty sidebar with no
// explanation and no way to recover.
const NavBannerWrap = styled.div`
  padding: 0 ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

// Task 9: gated nav items are now filtered OUT of the render entirely
// (isNavItemVisible above) rather than rendered with display:none — "hidden"
// per the user's ask means actually not present, not merely CSS-hidden.
const NavItemLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}14`};
  }

  &.active {
    background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}1f`};
    font-weight: 600;
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: -2px;
  }
`

// IMPORTANT-1 (final-review fix wave): the pre-AppShell `SidebarNav` carried
// `position: fixed; bottom: 0; overflow-y: auto`, so a nav taller than the
// viewport scrolled internally. AppShell's sidebar `View` only sets
// width/background/border (see @devnw/layout's AppShell.tsx createStyles),
// and react-native-web's base `View` style sets no `overflow` of its own
// (styles.view$raw has no `overflow` key, so it computes to the CSS initial
// value `visible`) — so with NO overflow rule anywhere in the new host, a
// full ~32-item nav (measured 1424px) simply renders past the sidebar's
// 846px box and spills onto the page background instead of scrolling.
//
// `overflow-y: auto` on THIS element is what actually fixes it, not merely a
// cosmetic scrollbar add-on: this <nav> is an (anonymous) flex item inside
// the sidebar View, which react-native-web sets to `display: flex;
// flex-direction: column` — and per the CSS Flexbox spec (§4.5, "Automatic
// Minimum Size of Flex Items"), a flex item's automatic minimum main size is
// its content size UNLESS its computed `overflow` is not `visible`, in which
// case the automatic minimum is 0. Content-sized minimum is exactly what was
// blocking the shrink here: the container has a definite, smaller height
// (stretched to match the content column), but the item couldn't shrink
// below its own content height, so it overflowed instead. Setting
// `overflow-y: auto` drops that floor to 0, letting the flexbox algorithm
// actually shrink this element to the container's height, at which point its
// own `overflow-y: auto` scrolls the excess internally instead of spilling
// onto the page.
//
// `min-height: 0` is added alongside for defense-in-depth. It is redundant
// in every evergreen engine this app targets (Chromium/Gecko/WebKit all
// implement the automatic-minimum-size rule above correctly, and have for
// years) — but it costs nothing and removes any dependence on that spec
// clause being applied correctly, which is the same belt-and-suspenders
// reasoning react-native-web itself uses by baking `minHeight: 0` directly
// into its own `View` base style rather than relying on `overflow` alone.
const NavRoot = styled.nav`
  overflow-y: auto;
  min-height: 0;
`

export interface AdminNavProps {
  /** Server-resolved admin_role claim (GET /v1/admin/whoami) — `string |
   *  null` to match AuthContextValue.adminRole exactly (NOT AdminRoleKind:
   *  an unscoped/backward-compat admin has adminRole === null, which is a
   *  load-bearing case in isNavItemVisible above, not an invalid value). */
  adminRole: string | null
  resolvedPermissions: string[] | null
  permissionsStatus: PermissionsStatus
  onRefreshPermissions: () => void
  /** Called after a nav item is activated, so the mobile drawer can close. */
  onNavigate?: () => void
}

/**
 * The admin nav list — permissions banner + role/permission-filtered
 * NAV_GROUPS. Rendered by BOTH AdminLayout's desktop AppShell sidebar slot
 * and its mobile off-canvas Sidebar drawer (see AdminLayout.tsx), so the two
 * surfaces can never drift apart: there is exactly one implementation of
 * "what nav items does this caller see".
 */
export const AdminNav = ({
  adminRole,
  resolvedPermissions,
  permissionsStatus,
  onRefreshPermissions,
  onNavigate,
}: AdminNavProps): JSX.Element => {
  return (
    <NavRoot aria-label="Admin navigation">
      {/* IMPORTANT-2: surface a whoami resolution failure IN the shell,
          with an accessible message and a retry — instead of silently
          rendering a shell with an empty (or stale) sidebar and no
          explanation. Shown alongside whatever nav resolvedPermissions
          still allows (last-known-good permissions, or none if whoami has
          never once resolved). */}
      {permissionsStatus === 'error' && (
        <NavBannerWrap>
          <PamError
            message={
              resolvedPermissions === null
                ? "Couldn't verify your permissions. Some navigation may be missing."
                : "Couldn't refresh your permissions. Showing your last known access."
            }
            onRetry={onRefreshPermissions}
          />
        </NavBannerWrap>
      )}
      {NAV_GROUPS.map((group) => {
        const slug = group.label
          ? group.label.toLowerCase().replace(/\s+/g, '-')
          : null
        const headerId = slug ? `nav-group-${slug}` : undefined
        return (
          <React.Fragment key={group.label ?? '__admin__'}>
            {group.label !== null && (
              <GroupHeader id={headerId}>{group.label}</GroupHeader>
            )}
            <div role="group" aria-labelledby={headerId}>
              {group.items
                .filter(({ to }) =>
                  isNavItemVisible(to, adminRole, resolvedPermissions)
                )
                .map(({ to, label, icon }) => (
                  <NavItemLink key={to} to={to} onClick={onNavigate}>
                    <Icon name={icon} ariaHidden size="sm" />
                    <span>{label}</span>
                  </NavItemLink>
                ))}
            </div>
          </React.Fragment>
        )
      })}
    </NavRoot>
  )
}
